import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../services/api'
import { useCurrentUser } from '../hooks/useAuth'
import SEOHead from '../components/SEOHead'
import { getStoredUtm } from '../utils/utm'
import type { Pet, Service, Appointment, Veterinarian } from '../types'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SPECIES = ['dog', 'cat', 'rabbit', 'bird', 'reptile', 'other'] as const

const today = new Date().toISOString().split('T')[0]

function fmt(priceCents: number) {
  return `$${(priceCents / 100).toFixed(0)}`
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionHeader({ step, title }: { step: number; title: string }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <span className="w-7 h-7 rounded-full bg-brand-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
        {step}
      </span>
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
    </div>
  )
}

function VetCard({
  vet,
  selected,
  onSelect,
}: {
  vet: Veterinarian
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`text-left p-4 rounded-xl border-2 transition-all flex gap-4 items-start ${
        selected
          ? 'border-brand-500 bg-brand-50 shadow-sm'
          : 'border-gray-200 hover:border-brand-300 hover:bg-gray-50'
      }`}
    >
      <img
        src={vet.avatar_url}
        alt={vet.name}
        className="w-14 h-14 rounded-full object-cover shrink-0 ring-2 ring-white shadow"
        onError={(e) => {
          // Fallback to initials avatar if portrait fails to load
          const target = e.currentTarget
          target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(vet.name)}&background=0ea5e9&color=fff&size=56`
        }}
      />
      <div className="min-w-0">
        <p className={`font-semibold text-sm ${selected ? 'text-brand-800' : 'text-gray-900'}`}>
          Dr. {vet.first_name} {vet.last_name}
        </p>
        <p className="text-xs text-gray-500 mt-0.5">
          {vet.years_experience} yrs experience · {vet.city}, {vet.state}
        </p>
        <div className="flex flex-wrap gap-1 mt-1.5">
          {vet.specialties.slice(0, 2).map((s) => (
            <span key={s} className="badge-sage text-xs">{s}</span>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">{vet.bio}</p>
      </div>
    </button>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function BookAppointmentPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const qc = useQueryClient()

  const { data: user, isLoading: authLoading } = useCurrentUser()
  const preselectedServiceId = searchParams.get('service') ?? ''

  // Redirect unauthenticated users to login, then back here
  useEffect(() => {
    if (!authLoading && !user) {
      const returnTo = encodeURIComponent(`/book/new${preselectedServiceId ? `?service=${preselectedServiceId}` : ''}`)
      navigate(`/login?redirect=${returnTo}`, { replace: true })
    }
  }, [user, authLoading, navigate, preselectedServiceId])

  // ── Remote data ────────────────────────────────────────────────────────────
  const { data: services = [] } = useQuery<Service[]>({
    queryKey: ['services'],
    queryFn: async () => (await apiClient.get<Service[]>('/services')).data,
  })

  const { data: pets = [], isLoading: petsLoading } = useQuery<Pet[]>({
    queryKey: ['pets'],
    queryFn: async () => (await apiClient.get<Pet[]>('/pets')).data,
    enabled: !!user,
  })

  // ── Form state ─────────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    service_id:      preselectedServiceId,
    pet_id:          '',
    veterinarian_id: '',
    date:            '',
    time:            '',
    address_street:  '',
    address_city:    '',
    address_state:   '',
    address_zip:     '',
  })

  // Pre-select first pet once loaded
  useEffect(() => {
    if (pets.length > 0 && !form.pet_id) {
      setForm((p) => ({ ...p, pet_id: String(pets[0].id) }))
    }
  }, [pets, form.pet_id])

  function setField(name: string, value: string) {
    setForm((p) => ({ ...p, [name]: value }))
  }

  // ── Vet search — triggered once city + state are both filled ───────────────
  const vetQueryEnabled = form.address_city.trim().length > 1 && form.address_state.trim().length === 2

  const { data: vets = [], isFetching: vetsLoading } = useQuery<Veterinarian[]>({
    queryKey: ['veterinarians', form.address_city.trim(), form.address_state.trim().toUpperCase()],
    queryFn: async () =>
      (
        await apiClient.get<Veterinarian[]>('/veterinarians', {
          params: {
            city:  form.address_city.trim(),
            state: form.address_state.trim().toUpperCase(),
          },
        })
      ).data,
    enabled: vetQueryEnabled,
    staleTime: 5 * 60 * 1000,
  })

  // Clear vet selection when city/state change
  useEffect(() => {
    setForm((p) => ({ ...p, veterinarian_id: '' }))
  }, [form.address_city, form.address_state])

  // ── Add-pet inline form ────────────────────────────────────────────────────
  const [showNewPet, setShowNewPet] = useState(false)
  const [newPet, setNewPet]         = useState({ name: '', species: 'dog', breed: '' })

  const addPetMutation = useMutation({
    mutationFn: async () => (await apiClient.post<Pet>('/pets', { pet: newPet })).data,
    onSuccess: (pet) => {
      qc.invalidateQueries({ queryKey: ['pets'] })
      setForm((p) => ({ ...p, pet_id: String(pet.id) }))
      setShowNewPet(false)
      setNewPet({ name: '', species: 'dog', breed: '' })
    },
  })

  // ── Book appointment ───────────────────────────────────────────────────────
  const [booked, setBooked]     = useState<Appointment | null>(null)
  const [apiErrors, setApiErrors] = useState<string[]>([])

  const bookMutation = useMutation({
    mutationFn: async () => {
      const scheduled_at = `${form.date}T${form.time}:00`
      return (
        await apiClient.post<Appointment>('/appointments', {
          appointment: {
            service_id:      Number(form.service_id),
            pet_id:          Number(form.pet_id),
            veterinarian_id: form.veterinarian_id ? Number(form.veterinarian_id) : undefined,
            scheduled_at,
            address_street:  form.address_street,
            address_city:    form.address_city,
            address_state:   form.address_state,
            address_zip:     form.address_zip,
          },
          // UTM tracking — attached so the CRM Lead's source/medium/campaign
          // reflects the campaign that drove this booking.
          utm: getStoredUtm(),
        })
      ).data
    },
    onSuccess: (appt) => {
      setBooked(appt)
      setApiErrors([])
    },
    onError: async (err: unknown) => {
      const resp = (err as { response?: { data?: { errors?: string[] } } }).response
      setApiErrors(resp?.data?.errors ?? ['Something went wrong. Please try again.'])
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setApiErrors([])
    bookMutation.mutate()
  }

  // ── Loading / redirecting ──────────────────────────────────────────────────
  if (authLoading || (!user && !authLoading)) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 rounded-full border-4 border-brand-300 border-t-brand-600 animate-spin" />
      </div>
    )
  }

  const selectedService = services.find((s) => String(s.id) === form.service_id)
  const selectedVet     = vets.find((v) => String(v.id) === form.veterinarian_id)

  // ── Success screen ─────────────────────────────────────────────────────────
  if (booked) {
    const dt = new Date(booked.scheduled_at)
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center">
        <div className="text-6xl mb-6">🎉</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">You're all set!</h1>
        <p className="text-gray-500 mb-8">
          Your appointment has been requested for{' '}
          <strong>{dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</strong>{' '}
          at <strong>{dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</strong>.
          We'll send a confirmation to <strong>{user.email}</strong>.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/" className="btn-secondary px-6 py-3">
            Back to Home
          </Link>
          <button
            onClick={() => {
              setBooked(null)
              setForm({ service_id: '', pet_id: '', veterinarian_id: '', date: '', time: '', address_street: '', address_city: '', address_state: '', address_zip: '' })
            }}
            className="btn-primary px-6 py-3"
          >
            Book Another Visit
          </button>
        </div>
      </div>
    )
  }

  // ── Booking form ───────────────────────────────────────────────────────────
  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <SEOHead title="Book a Visit" description="Schedule an in-home veterinary visit." noIndex />

      <div className="mb-8">
        <Link to="/book" className="text-sm text-brand-600 hover:underline">
          ← Back to services
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mt-3 mb-1">Book a Visit</h1>
        <p className="text-gray-500 text-sm">Fill in the details below and we'll confirm your appointment.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">

        {/* ── Step 1: Service ───────────────────────────────────────────────── */}
        <div className="card">
          <SectionHeader step={1} title="Select a service" />
          <div className="grid sm:grid-cols-2 gap-3">
            {services.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setField('service_id', String(s.id))}
                className={`text-left p-4 rounded-xl border-2 transition-all ${
                  form.service_id === String(s.id)
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200 hover:border-brand-300'
                }`}
              >
                <p className="font-semibold text-gray-900 text-sm">{s.name}</p>
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{s.description}</p>
                <p className="text-brand-700 font-bold text-sm mt-2">
                  {fmt(s.price_cents)}{' '}
                  <span className="text-gray-400 font-normal">· {s.duration_minutes} min</span>
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* ── Booking-as banner (signed-in user) ────────────────────────────── */}
        {user && (
          <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
            Booking as{' '}
            <span className="font-medium text-gray-800">
              {[user.first_name, user.last_name].filter(Boolean).join(' ')}
            </span>
            {' · '}
            <span>{user.email}</span>
          </div>
        )}

        {/* ── Step 2: Pet ───────────────────────────────────────────────────── */}
        <div className="card">
          <SectionHeader step={2} title="Which pet is this for?" />

          {petsLoading ? (
            <div className="h-10 bg-gray-100 animate-pulse rounded-lg" />
          ) : showNewPet ? (
            <div className="space-y-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-sm font-medium text-gray-700">Add a new pet</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="label">Name *</label>
                  <input
                    className="input"
                    required
                    value={newPet.name}
                    onChange={(e) => setNewPet((p) => ({ ...p, name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="label">Species *</label>
                  <select
                    className="input"
                    value={newPet.species}
                    onChange={(e) => setNewPet((p) => ({ ...p, species: e.target.value }))}
                  >
                    {SPECIES.map((s) => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="label">Breed (optional)</label>
                  <input
                    className="input"
                    value={newPet.breed}
                    onChange={(e) => setNewPet((p) => ({ ...p, breed: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => addPetMutation.mutate()}
                  disabled={!newPet.name || addPetMutation.isPending}
                  className="btn-primary text-sm px-5 py-2"
                >
                  {addPetMutation.isPending ? 'Saving…' : 'Save pet'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowNewPet(false)}
                  className="btn-secondary text-sm px-5 py-2"
                >
                  Cancel
                </button>
              </div>
              {addPetMutation.isError && (
                <p className="text-xs text-red-600">Failed to save pet. Please try again.</p>
              )}
            </div>
          ) : pets.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-3">
              {pets.map((p) => {
                const speciesEmoji = p.species === 'dog' ? '🐕' : p.species === 'cat' ? '🐈' : p.species === 'rabbit' ? '🐇' : p.species === 'bird' ? '🐦' : '🐾'
                const selected = form.pet_id === String(p.id)
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setField('pet_id', String(p.id))}
                    className={`text-left p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                      selected
                        ? 'border-brand-500 bg-brand-50 shadow-sm'
                        : 'border-gray-200 hover:border-brand-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-2xl">{speciesEmoji}</span>
                    <div>
                      <p className={`font-semibold text-sm ${selected ? 'text-brand-800' : 'text-gray-900'}`}>
                        {p.name}
                      </p>
                      <p className="text-xs text-gray-500 capitalize mt-0.5">{p.species}</p>
                    </div>
                  </button>
                )
              })}
              <button
                type="button"
                onClick={() => setShowNewPet(true)}
                className="text-left p-4 rounded-xl border-2 border-dashed border-gray-200 hover:border-brand-300 hover:bg-gray-50 transition-all flex items-center gap-3"
              >
                <span className="text-2xl">➕</span>
                <p className="font-semibold text-sm text-gray-600">Add a new pet +</p>
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <select
                className="input flex-1"
                required
                value={form.pet_id}
                onChange={(e) => {
                  if (e.target.value === '__new__') {
                    setShowNewPet(true)
                  } else {
                    setField('pet_id', e.target.value)
                  }
                }}
              >
                <option value="">-- No pets yet --</option>
                <option value="__new__">+ Add a new pet…</option>
              </select>
            </div>
          )}
        </div>

        {/* ── Step 3: Date & Time ───────────────────────────────────────────── */}
        <div className="card">
          <SectionHeader step={3} title="Date & time" />
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Date *</label>
              <input
                type="date"
                className="input"
                required
                min={today}
                value={form.date}
                onChange={(e) => setField('date', e.target.value)}
              />
            </div>
            <div>
              <label className="label">Time *</label>
              <input
                type="time"
                className="input"
                required
                value={form.time}
                onChange={(e) => setField('time', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ── Step 4: Address ───────────────────────────────────────────────── */}
        <div className="card">
          <SectionHeader step={4} title="Your address" />
          <div className="space-y-3">
            <div>
              <label className="label">Street address *</label>
              <input
                className="input"
                required
                placeholder="123 Main St"
                value={form.address_street}
                onChange={(e) => setField('address_street', e.target.value)}
              />
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-1">
                <label className="label">City *</label>
                <input
                  className="input"
                  required
                  value={form.address_city}
                  onChange={(e) => setField('address_city', e.target.value)}
                />
              </div>
              <div>
                <label className="label">State *</label>
                <input
                  className="input"
                  required
                  maxLength={2}
                  placeholder="CA"
                  value={form.address_state}
                  onChange={(e) => setField('address_state', e.target.value.toUpperCase())}
                />
              </div>
              <div>
                <label className="label">ZIP *</label>
                <input
                  className="input"
                  required
                  maxLength={10}
                  placeholder="90210"
                  value={form.address_zip}
                  onChange={(e) => setField('address_zip', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Step 5: Choose Your Vet ───────────────────────────────────────── */}
        <div className="card">
          <SectionHeader step={5} title="Choose your veterinarian" />

          {!vetQueryEnabled ? (
            <p className="text-sm text-gray-400 italic">
              Enter your city and state above to see available veterinarians in your area.
            </p>
          ) : vetsLoading ? (
            <div className="space-y-3">
              {[0, 1].map((i) => (
                <div key={i} className="h-24 bg-gray-100 animate-pulse rounded-xl" />
              ))}
            </div>
          ) : vets.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 text-sm">No veterinarians found in {form.address_city}, {form.address_state} yet.</p>
              <p className="text-gray-400 text-xs mt-1">We're expanding rapidly — check back soon, or continue without selecting a vet.</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-500 mb-3">
                {vets.length} veterinarian{vets.length !== 1 ? 's' : ''} available in {form.address_city}, {form.address_state}
              </p>
              <div className="space-y-3">
                {vets.map((vet) => (
                  <VetCard
                    key={vet.id}
                    vet={vet}
                    selected={form.veterinarian_id === String(vet.id)}
                    onSelect={() =>
                      setField('veterinarian_id',
                        form.veterinarian_id === String(vet.id) ? '' : String(vet.id)
                      )
                    }
                  />
                ))}
              </div>
              {form.veterinarian_id && (
                <button
                  type="button"
                  onClick={() => setField('veterinarian_id', '')}
                  className="mt-2 text-xs text-gray-400 hover:text-gray-600"
                >
                  Clear selection (any available vet)
                </button>
              )}
            </>
          )}
        </div>

        {/* ── Summary + submit ──────────────────────────────────────────────── */}
        {selectedService && (
          <div className="bg-brand-50 border border-brand-200 rounded-2xl px-5 py-4 text-sm text-brand-800">
            <div className="flex items-center justify-between gap-4 mb-2">
              <div>
                <p className="font-semibold">{selectedService.name}</p>
                <p className="text-brand-600 text-xs mt-0.5">{selectedService.duration_minutes} min visit</p>
              </div>
              <p className="text-2xl font-bold text-brand-700 shrink-0">{fmt(selectedService.price_cents)}</p>
            </div>
            {selectedVet && (
              <div className="flex items-center gap-2 pt-2 border-t border-brand-200">
                <img
                  src={selectedVet.avatar_url}
                  alt={selectedVet.name}
                  className="w-6 h-6 rounded-full object-cover"
                />
                <p className="text-xs text-brand-700">
                  with Dr. {selectedVet.first_name} {selectedVet.last_name}
                </p>
              </div>
            )}
          </div>
        )}

        {apiErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 space-y-1">
            {apiErrors.map((e) => <p key={e}>{e}</p>)}
          </div>
        )}

        <button
          type="submit"
          disabled={bookMutation.isPending || !form.service_id || !form.pet_id}
          className="btn-primary w-full py-4 text-base"
        >
          {bookMutation.isPending ? 'Booking…' : 'Confirm Booking'}
        </button>
      </form>
    </div>
  )
}
